"use client";

import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronUp, RefreshCw, ShieldCheck, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StatusIndicator } from "@/components/runyi/status-indicator";
import { resolveRunyiStatus, runyiStatusDotClasses } from "@/lib/status-indicator";

type ArchitectureHealth = {
  schema_version: number;
  architecture_version: string;
  release_status: string;
  source_commit: string;
  source_branch: string | null;
  version_authority: string;
  adr_registry: string;
  documentation_sync: string;
  architecture_gate: string;
  architecture_ci: string;
  canonical_release: string;
  canonical_architecture: string;
  drift: string[];
  generated_at: string;
};

const ARCHITECTURE_HEALTH_FILENAME = "runyi-architecture-health.json";
const ARCHITECTURE_HEALTH_SLOT_ID = "runyi-architecture-health-slot";

function architectureHealthEvidenceUrl() {
  if (typeof window === "undefined") return `/${ARCHITECTURE_HEALTH_FILENAME}`;
  const packagedAndroid = window.location.hostname === "appassets.androidplatform.net";
  const basePath = packagedAndroid ? "/assets/www/" : "/";
  return `${basePath}${ARCHITECTURE_HEALTH_FILENAME}?t=${Date.now()}`;
}

function compactCommit(value: string) {
  if (!value || value === "local-unverified") return "Unverified local build";
  return value.slice(0, 12);
}

function HealthRow({ label, status, detail }: { label: string; status: string; detail?: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 border-b border-foreground/7 py-2.5 last:border-b-0">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-foreground/72">{label}</p>
        {detail ? <p className="mt-0.5 truncate font-mono text-[9px] text-foreground/38">{detail}</p> : null}
      </div>
      <StatusIndicator status={status} compact />
    </div>
  );
}

export function ArchitectureHealthDock() {
  const [health, setHealth] = useState<ArchitectureHealth | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response = await fetch(architectureHealthEvidenceUrl(), { cache: "no-store" });
      if (!response.ok) throw new Error(`Architecture health evidence returned HTTP ${response.status}`);
      const payload = (await response.json()) as ArchitectureHealth;
      setHealth(payload);
    } catch (nextError) {
      setHealth(null);
      setError(nextError instanceof Error ? nextError.message : "Architecture health evidence is unavailable");
    } finally {
      setLastCheckedAt(new Date());
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(false);

    const interval = window.setInterval(() => void refresh(true), 60_000);
    const refreshWhenActive = () => void refresh(true);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refresh(true);
    };

    window.addEventListener("focus", refreshWhenActive);
    window.addEventListener("online", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshWhenActive);
      window.removeEventListener("online", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refresh]);

  useEffect(() => {
    const resolveSlot = () => setHeaderSlot(document.getElementById(ARCHITECTURE_HEALTH_SLOT_ID));
    resolveSlot();
    const observer = new MutationObserver(resolveSlot);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const overallStatus = useMemo(() => {
    if (loading) return "LOADING";
    if (error || !health) return "NOT_CONNECTED";
    if (health.drift.length > 0 || health.architecture_gate === "DRIFT") return "DRIFT";
    return health.architecture_gate || "UNKNOWN";
  }, [error, health, loading]);

  const statusPresentation = useMemo(() => resolveRunyiStatus(overallStatus), [overallStatus]);

  const evidenceDetail = useMemo(() => {
    if (!lastCheckedAt) return "Architecture evidence has not been checked yet";
    const checked = `Checked ${lastCheckedAt.toLocaleTimeString()}`;
    if (!health?.generated_at) return checked;
    return `${checked} · Evidence ${new Date(health.generated_at).toLocaleString()}`;
  }, [health?.generated_at, lastCheckedAt]);

  const headerControl = (
    <button
      type="button"
      onClick={() => setOpen((value) => !value)}
      className="relative inline-flex h-6 shrink-0 items-center gap-1.5 rounded-lg border border-foreground/10 bg-foreground/[0.025] px-1.5 text-foreground/60 transition hover:bg-foreground/[0.06] hover:text-foreground"
      aria-label={`Architecture health: ${overallStatus}`}
      aria-expanded={open}
      title={`Architecture Health${health?.architecture_version ? ` · v${health.architecture_version}` : ""} · ${overallStatus}`}
    >
      <ShieldCheck className="size-3.5" />
      <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${runyiStatusDotClasses[statusPresentation.tone]}`} />
      <span className="hidden text-[8px] font-bold uppercase tracking-[0.08em] md:inline">{statusPresentation.label || "UNKNOWN"}</span>
      <ChevronUp className={`hidden size-3 text-foreground/35 md:block ${open ? "rotate-180" : ""}`} />
    </button>
  );

  const panel = open && typeof document !== "undefined" ? createPortal(
    <div className="fixed inset-x-3 top-[68px] z-[120] mx-auto max-h-[calc(100dvh-5.25rem)] max-w-md overflow-y-auto overscroll-contain sm:left-auto sm:right-4 sm:mx-0 sm:w-[22rem]">
      <Card className="border-foreground/12 bg-background/98 py-0 shadow-2xl backdrop-blur-xl">
        <CardContent className="p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl border border-foreground/10 bg-foreground/[0.035]"><ShieldCheck className="size-4" /></span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold">Architecture Health</span>
                <span className="mt-0.5 block truncate text-[10px] text-foreground/42">Canonical governance evidence</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusIndicator status={overallStatus} compact />
              <button type="button" onClick={() => setOpen(false)} className="grid size-7 place-items-center rounded-lg text-foreground/45 hover:bg-foreground/[0.05] hover:text-foreground" aria-label="Close architecture health"><X className="size-3.5" /></button>
            </div>
          </div>

          {health ? (
            <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.018] px-3">
              <HealthRow label="Canonical architecture" status={health.release_status} detail={`v${health.architecture_version}`} />
              <HealthRow label="Architecture gate" status={health.architecture_gate} detail={health.canonical_release} />
              <HealthRow label="Version authority" status={health.version_authority} />
              <HealthRow label="ADR registry" status={health.adr_registry} />
              <HealthRow label="Documentation sync" status={health.documentation_sync} />
              <HealthRow label="Architecture CI evidence" status={health.architecture_ci} />
              <HealthRow label="Source commit" status={health.source_commit === "local-unverified" ? "UNKNOWN" : "VERIFIED"} detail={compactCommit(health.source_commit)} />
            </div>
          ) : (
            <div className="mt-3 rounded-xl border border-foreground/8 bg-foreground/[0.018] p-3 text-[11px] leading-5 text-foreground/50">
              {loading ? "Reading build-generated architecture evidence…" : error ?? "Architecture health evidence is unavailable."}
            </div>
          )}

          {health?.drift.length ? (
            <div className="mt-3 space-y-1.5 rounded-xl border border-foreground/10 p-3">
              <div className="flex items-center justify-between gap-2"><p className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/55">Detected drift</p><StatusIndicator status="DRIFT" compact /></div>
              {health.drift.map((item) => <p key={item} className="text-[10px] leading-4 text-foreground/55">{item}</p>)}
            </div>
          ) : null}

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-[9px] text-foreground/32">{evidenceDetail}</p>
            <button type="button" onClick={() => void refresh(false)} disabled={loading} className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg border border-foreground/10 px-2 text-[10px] font-semibold text-foreground/55 hover:bg-foreground/[0.04] disabled:opacity-50">
              <RefreshCw className={`size-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </CardContent>
      </Card>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {headerSlot ? createPortal(headerControl, headerSlot) : null}
      {panel}
    </>
  );
}
